import {
    Box,
    Button,
    Divider,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Stack,
    Text,
    VStack,
} from "@chakra-ui/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getChatLikes, getCommentLikes, getReplyLikes } from "../../api";
import LikeProfileListItem from "../../components/Chat/LikeProfileListItem";
import { colors } from "../../styles";
import { ILikeProfile, ILikeProfileList } from "../../types";

export type LikesTargetType = "chat" | "comment" | "reply";

interface LikesModalProps {
    isOpen: boolean;
    onClose: () => void;
    targetType: LikesTargetType;
    targetId: string;
}

export default function LikesModal({
    isOpen,
    onClose,
    targetType,
    targetId,
}: LikesModalProps) {
    const size = 10;

    const {
        data: likeProfileListData,
        isLoading: isLikeProfileListLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<ILikeProfileList>({
        queryKey: ["likes", targetType, targetId],
        queryFn: ({ pageParam }) => {
            const page = pageParam as number;

            if (targetType === "chat") {
                return getChatLikes({ chatId: targetId, page, size });
            }
            if (targetType === "comment") {
                return getCommentLikes({ commentId: targetId, page, size });
            }
            return getReplyLikes({ replyId: targetId, page, size });
        },
        initialPageParam: 1,
        enabled: isOpen && !!targetId,
        getNextPageParam: (lastPageData: ILikeProfileList) => {
            return lastPageData.hasNextPage ? lastPageData.currentPage + 1 : undefined;
        },
    });

    const likeProfiles: ILikeProfile[] =
        likeProfileListData?.pages.flatMap((page) => page.profiles) ?? [];

    const renderList = () => {
        if (isLikeProfileListLoading) {
            return (
                <VStack width="100%" minH="300px" justifyContent="center" alignItems="center" paddingY={10}>
                    <Spinner color={colors.blue} />
                </VStack>
            );
        }

        if (likeProfiles.length === 0) {
            return (
                <VStack width="100%" minH="300px" justifyContent="center" alignItems="center" paddingY={10}>
                    <Text fontSize={14} fontWeight={400} color={colors.g3}>
                        좋아요한 사용자가 없습니다.
                    </Text>
                </VStack>
            );
        }

        return (
            <Stack width="100%" minH="300px" spacing={0}>
                {likeProfiles.map((profile, index) => (
                    <Box key={profile.id}>
                        <LikeProfileListItem
                            avatar={profile.avatar}
                            nickname={profile.nickname}
                            userType={profile.user.type}
                        />
                        {index < likeProfiles.length - 1 && (
                            <Divider borderColor={colors.g5} />
                        )}
                    </Box>
                ))}
            </Stack>
        );
    };

    return (
        <Modal
            motionPreset="slideInBottom"
            size="md"
            isOpen={isOpen}
            onClose={onClose}
            isCentered
        >
            <ModalOverlay />
            <ModalContent borderRadius={16} overflow="hidden" maxH="70vh">
                <ModalHeader
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    borderBottom="0.2px solid"
                    borderColor={colors.g4}
                    padding={4}
                    fontSize={16}
                    fontWeight={700}
                    color={colors.g1}
                >
                    좋아요
                </ModalHeader>

                <ModalBody padding={4}>
                    <Box
                        minH="300px"
                        maxH="50vh"
                        overflowY="auto"
                        width="100%"
                    >
                        {renderList()}
                    </Box>

                    {hasNextPage && (
                        <Button
                            variant="ghost"
                            width="100%"
                            marginTop={2}
                            onClick={() => fetchNextPage()}
                            isLoading={isFetchingNextPage}
                        >
                            더보기
                        </Button>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
